import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  getLegalAidById,
  getLegalAidPdfBlob,
  getLegalAids,
  getLegalAidsByInjunctionId,
  getLegalAidsByImpositionId,
  getLegalAidsByPeriodId,
  openLegalAidDownload,
  updateLegalAid,
  type PaginatedResponse,
  type LegalAidEvidenceListModel,
  type LegalAidEvidenceModel,
  type UpdateLegalAidParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export type { UpdateLegalAidParams } from "@/api/cle-api";

export interface UseLegalAidsParams {
  userId?: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface UseLegalAidParams {
  userId?: string;
  legalAidId?: string;
}

export interface UseLegalAidsByPeriodParams {
  userId?: string;
  periodId?: string;
}

export interface UseLegalAidsByInjunctionParams {
  userId?: string;
  injunctionId?: string;
}

export interface UseLegalAidsByImpositionParams {
  userId?: string;
  impositionId?: string;
}

export interface DownloadLegalAidParams {
  userId: string;
  legalAidId: string;
}

export const legalAidsQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseLegalAidsParams) =>
  ["cle-api", "users", userId, "legal-aid", page, pageSize, reported] as const;

export const legalAidsByPeriodQueryKey = ({
  userId,
  periodId,
}: UseLegalAidsByPeriodParams) =>
  ["cle-api", "users", userId, "legal-aid", "by-period", periodId] as const;

export const legalAidsByInjunctionQueryKey = ({
  userId,
  injunctionId,
}: UseLegalAidsByInjunctionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "legal-aid",
    "by-injunction",
    injunctionId,
  ] as const;

export const legalAidsByImpositionQueryKey = ({
  userId,
  impositionId,
}: UseLegalAidsByImpositionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "legal-aid",
    "by-imposition",
    impositionId,
  ] as const;

export const legalAidQueryKey = ({ userId, legalAidId }: UseLegalAidParams) =>
  ["cle-api", "users", userId, "legal-aid", legalAidId] as const;

export const legalAidPdfQueryKey = ({
  userId,
  legalAidId,
}: UseLegalAidParams) =>
  ["cle-api", "users", userId, "legal-aid", legalAidId, "pdf"] as const;

export const useLegalAids = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseLegalAidsParams): UseQueryResult<
  PaginatedResponse<LegalAidEvidenceListModel>,
  Error
> =>
  useQuery({
    queryKey: legalAidsQueryKey({ userId, page, pageSize, reported }),
    queryFn: () =>
      getLegalAids({
        userId: userId ?? "",
        page,
        pageSize,
        reported,
      }),
    enabled: !!userId,
  });

export const useLegalAidsByPeriod = ({
  userId,
  periodId,
}: UseLegalAidsByPeriodParams): UseQueryResult<
  LegalAidEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: legalAidsByPeriodQueryKey({ userId, periodId }),
    queryFn: () =>
      getLegalAidsByPeriodId({
        userId: userId ?? "",
        periodId: periodId ?? "",
      }),
    enabled: !!userId && !!periodId,
  });

export const useLegalAidsByInjunction = ({
  userId,
  injunctionId,
}: UseLegalAidsByInjunctionParams): UseQueryResult<
  LegalAidEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: legalAidsByInjunctionQueryKey({ userId, injunctionId }),
    queryFn: () =>
      getLegalAidsByInjunctionId({
        userId: userId ?? "",
        injunctionId: injunctionId ?? "",
      }),
    enabled: !!userId && !!injunctionId,
  });

export const useLegalAidsByImposition = ({
  userId,
  impositionId,
}: UseLegalAidsByImpositionParams): UseQueryResult<
  LegalAidEvidenceListModel[],
  Error
> =>
  useQuery({
    queryKey: legalAidsByImpositionQueryKey({ userId, impositionId }),
    queryFn: () =>
      getLegalAidsByImpositionId({
        userId: userId ?? "",
        impositionId: impositionId ?? "",
      }),
    enabled: !!userId && !!impositionId,
  });

export const useLegalAid = ({
  userId,
  legalAidId,
}: UseLegalAidParams): UseQueryResult<LegalAidEvidenceModel, Error> =>
  useQuery({
    queryKey: legalAidQueryKey({ userId, legalAidId }),
    queryFn: () =>
      getLegalAidById({
        userId: userId ?? "",
        legalAidId: legalAidId ?? "",
      }),
    enabled: !!userId && !!legalAidId,
  });

export const useDownloadLegalAid = (): UseMutationResult<
  void,
  Error,
  DownloadLegalAidParams
> =>
  useMutation({
    mutationFn: openLegalAidDownload,
  });

export const useLegalAidPdf = ({
  userId,
  legalAidId,
}: UseLegalAidParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: legalAidPdfQueryKey({ userId, legalAidId }),
    queryFn: () =>
      getLegalAidPdfBlob({
        userId: userId ?? "",
        legalAidId: legalAidId ?? "",
      }),
    enabled: !!userId && !!legalAidId,
  });

export const useUpdateLegalAid = (): UseMutationResult<
  LegalAidEvidenceModel,
  Error,
  UpdateLegalAidParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLegalAid,
    onSuccess: (data, { legalAidId, userId }) => {
      queryClient.setQueryData<LegalAidEvidenceModel>(
        legalAidQueryKey({ userId, legalAidId }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(legalAidId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "legal-aid"],
      });
    },
  });
};
